import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import {
  DropdownMenu,
  Flex,
  IconButton,
  Reset,
  Text,
  TextField,
} from "@radix-ui/themes";

import type { GroupColorSlot } from "../workspace/workspace.types";
import type { GroupSelectRowView } from "./GroupSelect.controller";
import { ColorDot } from "./groupSelectRow.parts";
import { MenuRowPresentation } from "./MenuRow.pres";

export interface GroupRowPresentationProps {
  row: GroupSelectRowView;
  colorOptions: readonly GroupColorSlot[];
  onHoverChange: (hovered: boolean) => void;
  onSelect: () => void;
  onStartNameEdit: () => void;
  onDraftNameChange: (value: string) => void;
  onCommitName: () => void;
  onCancelNameEdit: () => void;
  onChangeGroupColor: (colorSlot: GroupColorSlot) => void;
  onRequestDelete: () => void;
}

const stopRowSelect = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

export const GroupRowPresentation = ({
  row,
  colorOptions,
  onHoverChange,
  onSelect,
  onStartNameEdit,
  onDraftNameChange,
  onCommitName,
  onCancelNameEdit,
  onChangeGroupColor,
  onRequestDelete,
}: GroupRowPresentationProps) => {
  const {
    group,
    letter,
    displayName,
    draftName,
    isActive,
    canDelete,
    memberCount,
    linkedWidgetsLabel,
    isEditing,
    showMembership,
    isSelectingForDelete,
    isSelected,
    isHovered,
  } = row;

  return (
    <MenuRowPresentation
      isActive={isActive}
      isHovered={isHovered}
      label={`Group ${letter} ${displayName}`}
      contentAlign={showMembership ? "start" : "center"}
      onHoverChange={onHoverChange}
      onSelect={onSelect}
    >
      {isSelectingForDelete && (
        <input
          type="checkbox"
          checked={isSelected}
          aria-label={`Select group ${letter}`}
          className="shrink-0"
          onPointerDown={stopRowSelect}
          onClick={stopRowSelect}
          onChange={() => onSelect()}
        />
      )}
      <Flex
        align="center"
        gap="4"
        className="group-select-dot-letter"
        data-group-nested
      >
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger>
            <Reset>
              <button
                type="button"
                className="group-select-color-trigger focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]"
                aria-label={`Change color for group ${letter}`}
                onPointerDown={stopRowSelect}
                onClick={stopRowSelect}
              >
                <ColorDot colorSlot={group.colorSlot} />
              </button>
            </Reset>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            side="left"
            align="start"
            size="1"
            variant="soft"
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            {colorOptions.map((slot) => (
              <DropdownMenu.Item
                key={slot}
                onSelect={() => onChangeGroupColor(slot)}
              >
                <Flex align="center" gap="2">
                  <ColorDot colorSlot={slot} />
                  <Text size="1" className="capitalize">
                    {slot}
                  </Text>
                </Flex>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        <Text size="1" className="atd-numeric group-select-letter-slot">
          {letter}
        </Text>
      </Flex>

      {isEditing ? (
        <TextField.Root
          size="1"
          value={draftName}
          aria-label={`Name for group ${letter}`}
          className="min-w-0 flex-1"
          autoFocus
          onPointerDown={stopRowSelect}
          onClick={stopRowSelect}
          onChange={(event) => onDraftNameChange(event.target.value)}
          onBlur={onCommitName}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onCommitName();
            }
            if (event.key === "Escape") {
              onCancelNameEdit();
            }
          }}
        />
      ) : (
        <Flex direction="column" gap="0" className="min-w-0 flex-1">
          <Text
            size="1"
            weight="bold"
            color="gray"
            truncate
            className="min-w-0"
          >
            {displayName}
          </Text>
          {linkedWidgetsLabel && (
            <Text size="1" color="gray" className="min-w-0" truncate>
              {memberCount > 0 && (
                <span data-testid="group-member-count">{memberCount}</span>
              )}
              {memberCount > 0 ? " · " : ""}
              {linkedWidgetsLabel}
            </Text>
          )}
        </Flex>
      )}

      {!isEditing && !linkedWidgetsLabel && memberCount > 0 && (
        <Text
          size="1"
          color="gray"
          data-testid="group-member-count"
          className="shrink-0"
        >
          {memberCount}
        </Text>
      )}

      {!isSelectingForDelete && !isEditing && (
        <Flex
          className="group-select-edit"
          data-group-nested
          onPointerDown={stopRowSelect}
          onClick={stopRowSelect}
        >
          <IconButton
            type="button"
            variant="ghost"
            color="gray"
            size="1"
            aria-label={`Edit name for group ${letter}`}
            onClick={onStartNameEdit}
          >
            <PencilSimpleIcon size={14} aria-hidden />
          </IconButton>
        </Flex>
      )}

      {!isSelectingForDelete && (
        <Flex
          className="group-select-delete"
          data-group-nested
          onPointerDown={stopRowSelect}
          onClick={stopRowSelect}
        >
          <IconButton
            type="button"
            variant="ghost"
            color="red"
            size="1"
            aria-label={`Delete group ${letter}`}
            disabled={!canDelete}
            onClick={onRequestDelete}
          >
            <TrashIcon size={14} aria-hidden />
          </IconButton>
        </Flex>
      )}
    </MenuRowPresentation>
  );
};
